import React, { useState, useEffect, useRef } from "react";

import useFetch from "../../hooks/useFetch";
import {
  encodeURL,
  capitalizeWords,
  applyDiscountRules,
  getColorAndSize,
  getButton,
  calculateWeight,
  getShippingRate,
  calculateFreeShipping,
  generateInvoiceNumber,
  generatePackageID,
} from "../../helpers/common";
import { calculateOrderTotals } from "../../helpers/orderTotals";
import { makeRequest } from "../../makeRequest";
import { useParams, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { addToCart } from "../../redux/cartReducer";
import { openCart } from "../../redux/cartVisibilityReducer";
import { setOrderID, addShippingData } from "../../redux/checkoutReducer";
import { setDiscountRules } from "../../redux/discountRulesReducer";

import "./Product.scss";
import { ReactComponent as UAEDirham } from "../../SVGs/UAEDirham.svg";
import { ReactComponent as Share } from "../../SVGs/Share.svg";
import { ReactComponent as Whatsapp } from "../../SVGs/Whatsapp.svg";
import { ReactComponent as Close } from "../../SVGs/CloseWhite.svg";

import FeaturedProducts from "../../components/FeaturedProducts/FeaturedProducts";
import LoaderOverlay from "../../components/LoaderOverlay/LoaderOverlay";
import ProductOptions from "../../components/ProductOptions/ProductOptions";
import ProductDetails from "../../components/ProductDetails/ProductDetails";
import SizeTable from "../../components/SizeTable/SizeTable";
import { enable } from "../../redux/overlayReducer";
import useDeviceType from "../../hooks/useDeviceType";
import useDHLShipping from "../../hooks/useDHLShipping";

import { useTranslation } from "react-i18next";
import { Helmet } from "react-helmet-async";

const Product = () => {
  const slug = useParams().slug;
  const dispatch = useDispatch();
  const deviceType = useDeviceType();
  const { t, i18n } = useTranslation();
  const currentLanguage = i18n.language;
  const imageGallery = useRef(null);

  const [metaTitle, setMetaTitle] = useState("");
  const [metaDescription, setMetaDescription] = useState("");
  const [metaKeywords, setMetaKeywords] = useState("");

  const [productInfo, setProductInfo] = useState({});
  const [isProductLoading, setIsProductLoading] = useState(false);
  const [isProductError, setIsProductError] = useState(false);
  const [productRetries, setProductRetries] = useState(0);

  const [barcode, setBarcode] = useState("");
  const [images, setImages] = useState([]);
  const [selectedVariantId, setSelectedVariantId] = useState(0);
  const [selectedButtonId, setSelectedButtonId] = useState(null);
  const [isOutOfStock, setIsOutOfStock] = useState(false);

  const [imageGalleryOpen, setImageGalleryOpen] = useState(false);
  const [image, setImage] = useState("");
  const [isZooming, setIsZooming] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [instantBuyError, setInstantBuyError] = useState("");
  const [instantBuyInlineError, setInstantBuyInlineError] = useState("");   // ADD
  const [isProductUnavailable, setIsProductUnavailable] = useState(false);
  const [showLimitMessage, setShowLimitMessage] = useState(false);

  const discountRules = useSelector((state) => state.discountRules);
  const cartProds = useSelector((state) => state.cart.products);
  const user = useSelector((state) => state.user.user);
  const navigate = useNavigate();
  const { getRates: getDHLRates } = useDHLShipping();

  const productBaseUrl = "/products";
  const productParams = new URLSearchParams({
    populate: "deep,4",
    locale: "en",
    "filters[Status][$eq]": true,
    "filters[SEOURL][$eq]": slug,
  });
  if (productRetries > 0) productParams.set("_retry", productRetries);
  const productUrl = `${productBaseUrl}?${productParams.toString()}`;
  const {
    data: productData,
    loading: productLoading,
    error: productError,
  } = useFetch(productUrl);

  const retryProduct = () => setProductRetries((prev) => prev + 1);

  const openImage = (imageURL) => {
    setImageGalleryOpen(true);
    setImage(imageURL);
  };

  const closeImage = () => {
    setImageGalleryOpen(false);
    setImage("");
  };

  const toggleShare = () => {
    if (navigator.share) {
      navigator.share({
        title: document.title,
        url: window.location.href,
      });
    }
  };

  const zoomMouseClick = (e) => {
    if (deviceType === "mobile") return;

    if (isZooming) {
      setIsZooming(false);
      imageGallery.current.style.transform = `scale(1) ${currentLanguage == "en" ? "translateX(-50%)" : "translateX(50%)"
        }`;
      imageGallery.current.style.transformOrigin = "center center";
      imageGallery.current.style.cursor = "zoom-in";
    } else {
      setIsZooming(true);
      const { clientX, clientY, target } = e;
      const { width, height, left, top } = target.getBoundingClientRect();

      const x = clientX - left;
      const y = clientY - top;

      const xPercent = (x / width) * 100;
      const yPercent = (y / height) * 100;

      imageGallery.current.style.transformOrigin = `${xPercent}% ${yPercent}%`;
      imageGallery.current.style.transform = `scale(2)`;
      imageGallery.current.style.cursor = "zoom-out";
    }
  };

  const zoomMouseMove = (e) => {
    if (!isZooming) return;

    const { clientX, clientY, target } = e;
    const { width, height, left, top } = target.getBoundingClientRect();

    const x = clientX - left;
    const y = clientY - top;

    const xPercent = (x / width) * 100;
    const yPercent = (y / height) * 100;

    imageGallery.current.style.transformOrigin = `${xPercent}% ${yPercent}%`;
    imageGallery.current.style.transform = `scale(2)`;
  };

  const addProduct = () => {
    if (!barcode) return;
    const selectedVariant = productInfo?.attributes?.Variants?.find(
      (variant) => variant?.id == selectedVariantId,
    );
    if (selectedVariant) {
      const selectedSize = selectedVariant?.Sizes?.find(
        (size) => size?.Barcode == barcode,
      );

      if (selectedSize) {
        if (cartProds?.length > 0) {
          const totalQtyForBarcode = cartProds
            .filter((cp) => cp?.barcode == selectedSize?.Barcode)
            .reduce((sum, cp) => sum + (cp?.quantity || 0), 0);
          // if (totalQtyForBarcode >= selectedSize?.Quantity) return;
          if (totalQtyForBarcode >= selectedSize?.Quantity) {
            setShowLimitMessage(true);
            return;
          }
        }
      }
    }
    setShowLimitMessage(false);

    dispatch(
      addToCart({
        id: productInfo?.id,
        quantity: 1,
        barcode: barcode,
        selectedVariantId: selectedVariantId,
        selectedButtonId: selectedButtonId,
        image: productInfo?.attributes?.DefaultImage?.data?.id,
      }),
    );
    dispatch(openCart());
    dispatch(enable());
  };

  const handleInstantBuy = async () => {
    if (!user?.id) {
      navigate(`/login?redirect=/product/${slug}`);
      return;
    }

    setIsProcessing(true);
    setInstantBuyError("");
    setInstantBuyInlineError("");   // ADD

    try {
      // 🟢 1. get address
      // const addressResponse = await makeRequest.get(
      //   `/addresses?populate=deep&filters[User][id][$eq]=${user.id}&sort=updatedAt:desc`,
      // );
      const addressParams = new URLSearchParams({
        "filters[User][id][$eq]": user.id,
        sort: "updatedAt:desc",
        "pagination[limit]": 1,
        "populate[Country][fields][0]": "Name",
        "populate[City][fields][0]": "Name",
        "populate[Area][fields][0]": "Name",
      });
      const addressResponse = await makeRequest.get(
        `/addresses?${addressParams.toString()}`,
      );
      const savedAddress = addressResponse?.data?.data?.[0];

      if (!savedAddress) {
        navigate("/address-book");
        setIsProcessing(false);
        return;
      }

      const addr = savedAddress.attributes;

      // Block instant buy if the saved address has no city/country (e.g. its city was deleted)
      if (!addr.City?.data?.id || !addr.Country?.data?.id) {
        setInstantBuyInlineError(t("product.address-city-missing"));   // CHANGED
        setIsProcessing(false);                                        // ADDED
        setTimeout(() => setInstantBuyInlineError(""), 8000);          // ADDED
        return;
      }
      // 🟢 2. shipping calculation
      const shippingCacheBust = Date.now();
      const providersParams = new URLSearchParams({
        populate: "*",
        locale: "en",
        "filters[Status][$eq]": true,
        _: shippingCacheBust,
      });
      // const ratesParams = new URLSearchParams({
      //   populate: "deep",
      //   "pagination[limit]": -1,
      //   _: shippingCacheBust,
      // });
            const ratesParams = new URLSearchParams({
        "pagination[limit]": -1,
        "populate[ShippingProvider][fields][0]": "Name",
        "populate[Areas][populate][Country][fields][0]": "Name",
        "populate[Areas][populate][City][fields][0]": "Name",
        "populate[Areas][populate][Areas][fields][0]": "Name",
        _: shippingCacheBust,
      });
      const [providersRes, ratesRes] = await Promise.all([
        makeRequest.get(`/shipping-providers?${providersParams.toString()}`),
        makeRequest.get(`/shipping-rates?${ratesParams.toString()}`),
      ]);

      const shippingProviders = (providersRes?.data?.data || []).filter(
        (provider) => provider?.attributes?.Status !== false,
      );
      const shippingRatesArr = ratesRes?.data?.data || [];

      const countryId = addr.Country?.data?.id;
      const cityId = addr.City?.data?.id;
      const areaId = addr.Area?.data?.id || 0;
      const countryName = addr.Country?.data?.attributes?.Name;
      const cityName = addr.City?.data?.attributes?.Name;
      const postalCode = addr.Postcode || "";

      const productsForShipping = [{ product: productInfo, qty: 1, barcode, selectedVariantId }];

      let shippingMethod = "";
      let shippingCost = 0;
      let shippingProductCode = "";
      const isUAE = countryId == 231;
      const UAELocalProviders = ["Classy YA", "Waddeely", "SMSA - By Air", "SMSA - By Road"];
      const netGlobalEnabled = shippingProviders.some(
        (provider) => provider?.attributes?.Name === "The Net Global",
      );

      if (isUAE) {
        for (const provider of shippingProviders) {
          const name = provider?.attributes?.Name;
          if (name === "DHL" || name === "The Net Global") continue;
          const rate = getShippingRate(name, shippingRatesArr, countryId, cityId, areaId, productsForShipping);
          if (rate > 0) {
            shippingMethod = name;
            shippingCost = rate;
            break;
          }
        }
      } else {
        const settingsRes = await makeRequest.get(
          `/settings?populate=*&pagination[limit]=-1&_=${Date.now()}`
        );
        const settings = settingsRes?.data?.data || [];
        const dhlProvider = shippingProviders.find(
          (provider) => provider?.attributes?.Name === "DHL",
        );

        if (!netGlobalEnabled) {
          if (dhlProvider) {
            try {
              const weight = calculateWeight(productsForShipping);
              const [dhlRate, , dhlProductCode] = await getDHLRates(
                weight,
                countryName,
                cityName,
                postalCode,
                settings,
              );
              if (dhlRate > 0) {
                shippingMethod = "DHL";
                shippingCost = Math.ceil(dhlRate);
                shippingProductCode = dhlProductCode || "";
              }
            } catch (e) {
              shippingCost = 0;
            }
          }
        } else {
          for (const provider of shippingProviders) {
            const name = provider?.attributes?.Name;
            if (UAELocalProviders.includes(name)) continue;
            if (name === "DHL") {
              try {
                const weight = calculateWeight(productsForShipping);
                const [dhlRate, , dhlProductCode] = await getDHLRates(
                  weight,
                  countryName,
                  cityName,
                  postalCode,
                  settings,
                );
                if (dhlRate > 0) {
                  shippingMethod = "DHL";
                  shippingCost = Math.ceil(dhlRate);
                  shippingProductCode = dhlProductCode || "";
                  break;
                }
              } catch (e) {
                continue;
              }
            } else {
              const rate = getShippingRate(name, shippingRatesArr, countryId, cityId, areaId, productsForShipping);
              if (rate > 0) {
                shippingMethod = name;
                shippingCost = rate;
                break;
              }
            }
          }
        }
      }

      if (!shippingMethod) {
        if (isUAE) {
          shippingMethod = "Classy YA";
          shippingCost = 0;
        } else {
          setIsProcessing(false);
          navigate("/");
          return;
        }
      }

      // Re-fetch active discount rules so a rule disabled/expired after this
      // page loaded can no longer be applied to this instant-buy order (item
      // discounts and free shipping both). On refetch failure we default to an
      // EMPTY rule set (never stale cached rules) so a disabled rule cannot be
      // re-applied to the order; the order still goes through.
      let effectiveDiscountRules = {
        isLoading: false,
        isError: false,
        rules: [],
      };
      try {
        const rulesParams = new URLSearchParams({
          populate: "deep",
          "filters[Status][$eq]": true,
          // cache-bust so a rule toggled in the admin (e.g. free shipping) is
          // applied on Buy Now/instant-buy even if the HTTP/CDN layer cached
          // an older response on live
          _: Date.now(),
        });
        const rulesRes = await makeRequest.get(
          `/discount-rules?${rulesParams.toString()}`
        );
        if (Array.isArray(rulesRes?.data?.data)) {
          effectiveDiscountRules = {
            isLoading: false,
            isError: false,
            rules: rulesRes.data.data,
          };
          dispatch(setDiscountRules(rulesRes.data.data));
        }
      } catch (e) {
        console.error("Failed to refresh discount rules:", e);
      }

      // Re-fetch fresh product data to ensure latest price before order
      let orderProductInfo = productInfo;
      try {
        const freshRes = await makeRequest.get(
          `/products?populate=deep,4&locale=en&filters[Status][$eq]=true&filters[SEOURL][$eq]=${slug}&_=${Date.now()}`
        );
        if (freshRes?.data?.data?.length > 0) {
          let freshProduct = freshRes.data.data[0];
          if (currentLanguage != "en") {
            const localization = freshProduct?.attributes?.localizations?.data?.find(
              (local) => local?.attributes?.locale === currentLanguage,
            );
            if (localization) {
              freshProduct = {
                ...freshProduct,
                attributes: {
                  ...freshProduct?.attributes,
                  ...localization?.attributes,
                  SizeGuide: productInfo?.attributes?.SizeGuide,
                },
              };
            }
          }
          const [discounted] = applyDiscountRules(
            [structuredClone(freshProduct)], effectiveDiscountRules, selectedVariantId, barcode
          );
          orderProductInfo = discounted;
        } else if (freshRes?.data?.data?.length === 0) {
          // Product was unpublished/removed in Strapi → block the purchase
          // and show the existing "Product Not Found" UI.
          setIsProcessing(false);
          setInstantBuyError("");
          setIsProductUnavailable(true);
          return;
        }
      } catch (e) {
        console.error("Failed to refresh product for instant buy:", e);
      }

      const freshVariant = orderProductInfo?.attributes?.Variants?.find(
        (v) => v?.id == selectedVariantId
      );
      const freshSize = freshVariant?.Sizes?.find(
        (s) => s?.Barcode == barcode
      );
      if (!freshSize || freshSize?.Quantity <= 0) {
        setIsProcessing(false);
        navigate("/");
        return;
      }

      // 🟢 3. totals (same checkout logic)
      const prodPrice = orderProductInfo?.attributes?.Price || 0;
      // subtotal for the free-shipping check must use the discounted price the
      // customer actually pays (same convention as checkout) so a MinimumSubtotal
      // rule is evaluated against the same amount the order is built from.
      const instaSubtotal =
        orderProductInfo?.attributes?.discountedPrice != null
          ? orderProductInfo?.attributes?.discountedPrice
          : prodPrice;
      const isFreeShipping = calculateFreeShipping(productsForShipping, effectiveDiscountRules, instaSubtotal, shippingMethod, countryId);
      const effectiveShippingCost = isFreeShipping ? 0 : shippingCost;
      const realShippingCost = isFreeShipping ? shippingCost : effectiveShippingCost;
      const totalsObj = calculateOrderTotals({
        products: [
          {
            product: orderProductInfo,
            qty: 1,
            barcode,
            selectedVariantId,
          },
        ],
        country: countryId,
        shippingValue: effectiveShippingCost,
        hasFreeShipping: isFreeShipping,
      });

      const totalsArr = [
        { TotalName: "Net Amount", TotalValue: totalsObj.subtotal.toFixed(2) },
        { TotalName: "VAT", TotalValue: isUAE ? totalsObj.vat.toFixed(2) : "0.00" },
        { TotalName: "Subtotal", TotalValue: prodPrice.toFixed(2) },
        { TotalName: "Shipping", TotalValue: realShippingCost.toFixed(2) },
        ...(isFreeShipping
          ? [{ TotalName: "Discount - Free Shipping", TotalValue: realShippingCost.toFixed(2) }]
          : []),
        { TotalName: "Total", TotalValue: totalsObj.total.toFixed(2) },
      ];

      // 🟢 3. product structure (same checkout style)
      const [color, size] = getColorAndSize(
        orderProductInfo,
        barcode,
        selectedVariantId,
      );
      const button = getButton(orderProductInfo, selectedButtonId, selectedVariantId, currentLanguage);
      const prodPriceWithoutVAT = isUAE ? parseFloat((prodPrice / 1.05).toFixed(2)) : prodPrice;
      const vatAmount = isUAE ? prodPrice - prodPriceWithoutVAT : 0;

      const discountAmount =
        orderProductInfo?.attributes?.discountedPrice != null
          ? (prodPrice - orderProductInfo?.attributes?.discountedPrice) * 1
          : 0;

      const productsArr = [
        {
          ProductName: orderProductInfo?.attributes?.Title,
          PriceBeforeVAT: prodPriceWithoutVAT,
          PriceAfterVAT: prodPrice,
          Quantity: 1,
          Product: orderProductInfo?.id,
          Barcode: barcode,
          Size: size,
          Color: color,
          Button: button,
          DiscountAmount: discountAmount,
          VAT: parseFloat(vatAmount.toFixed(2)),
          GrossAmount: prodPrice,
          Weight: parseFloat((calculateWeight([{ product: orderProductInfo, qty: 1, barcode, selectedVariantId }], false) || 0.5).toFixed(2)),
          Image: orderProductInfo?.attributes?.DefaultImage?.data?.id,
        },
      ];

      // 🟢 4. build request (IMPORTANT PLACE)
      const placeOrderRequest = {
        User: user?.id,
        Email: user?.email || "no-email@classy-ym.com",
        FirstName: addr.FirstName || "",
        LastName: addr.LastName || "",
        Address1: addr.Address1,
        Address2: addr.Address2,
        PostalCode: addr.Postcode,
        City: addr.City?.data?.attributes?.Name,
        Country: addr.Country?.data?.attributes?.Name,
        Area: addr.Area?.data?.attributes?.Name || "All Areas",
        AddressDisplayName: addr.DisplayName || "",
        AddressMapLink: addr.MapLink || "",

        PaymentMethod: "Network International",
        ShippingMethod: shippingMethod,
        PaymentResponse: "",
        Status: "Draft",


        TotalBeforVAT: totalsObj.totalBeforeVAT,
        TotalAfterVAT: totalsObj.total,
        Phone: user?.PhoneNumber || "",
        ShippingCost: realShippingCost.toFixed(2),
        IsGift: false,
        GiftMessage: "",
        Notes: "Instant Buy",
        SpecialRequest: "",
        VoucherCode: "",
        CouponCode: "",
        DicountAmount: "0",

        Products: productsArr,
        Totals: totalsArr,
        Packages: [{
          Number: generatePackageID(1),
          Weight: parseFloat((calculateWeight([{ product: orderProductInfo, qty: 1, barcode, selectedVariantId }], false) || 0.5).toFixed(2)),
          Products: [{
            ProductName: orderProductInfo?.attributes?.Title,
            Quantity: 1,
            Barcode: barcode,
            Color: color,
            Size: size,
          }]
        }],
        shippingResponse: "",
      };
      console.log("🔥 PLACE ORDER REQUEST:");
      console.log(placeOrderRequest);
      console.log("📧 EMAIL BEING SENT:", placeOrderRequest.Email, "| user.email:", user?.email);
      // 🟢 5. send to Strapi
      const orderResponse = await makeRequest.post("/orders", {
        data: placeOrderRequest,
      });
      console.log("✅ STRAPI RESPONSE:");
      console.log(orderResponse?.data);
      const orderId = orderResponse?.data?.data?.id;
      console.log("🆔 ORDER ID:", orderId);
      if (!orderId) throw new Error("Order creation failed");

      const invoiceNum = generateInvoiceNumber(orderId);
      await makeRequest.put(`/orders/${orderId}`, { data: { InvoiceNumber: invoiceNum } });

      dispatch(setOrderID(orderId));
      dispatch(addShippingData({
        shippingMethod,
        shippingProductCode,
        countryName,
        cityName,
        postalCode,
        addressLine1: addr.Address1,
        addressLine2: addr.Address2,
        customerName: `${addr.FirstName} ${addr.LastName}`,
        customerEmail: user?.email || "no-email@classy-ym.com",
        customerPhone: user?.PhoneNumber || "",
        total: Number(totalsObj.total.toFixed(2)),
        totalCurrency: "AED",
        description: `Order #${orderId} - Instant Buy`,
        pkgs: [{
          Weight: parseFloat((calculateWeight([{ product: orderProductInfo, qty: 1, barcode, selectedVariantId }], false) || 0.5).toFixed(2)),
        }],
        orderID: orderId,
        deliveryNotes: "",
        lineItems: [{
          number: 1,
          description: orderProductInfo?.attributes?.Title || "Product",
          price: prodPrice,
          quantity: { value: 1, unitOfMeasurement: "PCS" },
          commodityCodes: [
            { typeCode: "outbound", value: "61044300" },
            { typeCode: "inbound", value: "61044300" },
          ],
          manufacturerCountry: "AE",
          weight: {
            netValue: parseFloat((calculateWeight([{ product: orderProductInfo, qty: 1, barcode, selectedVariantId }], false) || 0.5).toFixed(2)),
            grossValue: parseFloat((calculateWeight([{ product: orderProductInfo, qty: 1, barcode, selectedVariantId }], false) || 0.5).toFixed(2)),
          },
        }],
        invoiceNum: generateInvoiceNumber(orderId),
        pkgDimensions: {},
      }));

      // 🟢 6. payment
      const totalValue = totalsArr.find((t) => t.TotalName === "Total")?.TotalValue;
      if (!totalValue || parseFloat(totalValue) <= 0) {
        throw new Error("Invalid order total. Please try again.");
      }

      const paymentResponse = await makeRequest.post("/ngenuis", {
        amount: totalValue,
        currency: "AED",
        emailAddress: user?.email,
        orderId: orderId.toString(),
        language: currentLanguage,
        cancelUrl: window.location.origin + `/product/${slug}`,
      });

      const paymentUrl = paymentResponse?.data?.data;

      if (paymentUrl) {
        sessionStorage.setItem("instantBuyOrderId", orderId.toString());
        window.location.href = paymentUrl;
      } else {
        throw new Error("No payment URL received");
      }
    } catch (error) {
      console.error("Instant Buy Error:", error);

      setInstantBuyError(
        error?.response?.data?.error?.message || "Something went wrong",
      );

      setIsProcessing(false);
    }
  };

  useEffect(() => {
    const onPageShow = (event) => {
      if (event.persisted) {
        setIsProcessing(false);
        setInstantBuyError("");
      }
    };
    window.addEventListener("pageshow", onPageShow);
    return () => window.removeEventListener("pageshow", onPageShow);
  }, []);

  useEffect(() => {
    if (!isProcessing || instantBuyError) return;
    const timer = setTimeout(() => {
      setIsProcessing(false);
      setInstantBuyError("Something went wrong. Please try again.");
    }, 45000);
    return () => clearTimeout(timer);
  }, [isProcessing, instantBuyError]);

  useEffect(() => {
    if (!productLoading && productData?.length > 0) {
      let product = productData[0];
      if (currentLanguage != "en") {
        const localization = product?.attributes?.localizations?.data?.find(
          (local) => local?.attributes?.locale === currentLanguage,
        );
        if (localization) {
          product = {
            ...product,
            attributes: {
              ...product?.attributes,
              ...localization?.attributes,
              SizeGuide: product?.attributes?.SizeGuide,
            },
          };
        }
      }
      const newProducts = applyDiscountRules(
        [product],
        discountRules,
        selectedVariantId,
        barcode,
      );
      setProductInfo(newProducts[0]);
      setMetaTitle(newProducts[0]?.attributes?.SEO?.metaTitle);
      setMetaDescription(newProducts[0]?.attributes?.SEO?.metaDescription);
      setMetaKeywords(newProducts[0]?.attributes?.SEO?.keywords);
      setIsProductLoading(false);
    } else if (!productLoading && productData?.length == 0) {
      setIsProductLoading(false);
    } else if (productLoading && !productError) {
      setIsProductLoading(true);
    } else if (!productLoading && productError && !productData) {
      setIsProductLoading(false);
      setIsProductError(true);
    }
  }, [productLoading, currentLanguage]);

  useEffect(() => {
    if (!productInfo) return;
    const variant = productInfo?.attributes?.Variants.find(
      (variant) => variant.id === selectedVariantId,
    );
    if (variant) {
      let newImages = variant?.Images?.data;
      if (newImages?.length === 0) {
        newImages = productInfo?.attributes?.Images?.data;
      }
      setImages(newImages);
    }
    const discountedProduct = applyDiscountRules(
      [productInfo],
      discountRules,
      selectedVariantId,
      barcode,
    );
    setProductInfo((prev) => ({ ...prev, ...discountedProduct[0] }));
  }, [barcode]);

  useEffect(() => {
    if (isOutOfStock) {
      let newImages = productInfo?.attributes?.Images?.data;
      if (!newImages) {
        newImages = [productInfo?.attributes?.DefaultImage?.data];
      }
      setImages(newImages);
    }
  }, [isOutOfStock]);

  useEffect(() => {
    setProductInfo({});
    setImages([]);
    setSelectedVariantId(0);
    setSelectedButtonId(null);
    setBarcode("");
    setIsOutOfStock(false);
    setMetaTitle("");
    setMetaDescription("");
    setMetaKeywords("");
    setProductRetries(0);
    setIsProductError(false);
    setIsProductLoading(false);
    setIsProductUnavailable(false);
    setShowLimitMessage(false);
  }, [slug]);
  useEffect(() => {              // NEW
    setShowLimitMessage(false);  // NEW
  }, [barcode, cartProds]);
  useEffect(() => {
    document.title = `${t("general.classy-ym")} - ${t(
      "product.product",
    )} ${capitalizeWords(slug)}`;
  }, [productInfo]);

  const [currentSlide, setCurrentSlide] = useState(0);
  const sliderRef = useRef(null);
  let isDragging = false;
  let startX = 0;
  let draggedAmount = 0;

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev === 0 ? images.length - 1 : prev - 1));
  };

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev === images.length - 1 ? 0 : prev + 1));
  };

  // Mouse Handlers
  const handleMouseDown = (e) => {
    if (deviceType !== "mobile") return;
    isDragging = true;
    startX = e.clientX;
  };

  const handleMouseMove = (e) => {
    if (!isDragging || deviceType !== "mobile") return;
    const deltaX = e.movementX;
    sliderRef.current.style.transform = `translateX(calc(${currentLanguage == "ar" ? "+" : "-"
      }${currentSlide * 100}% + ${deltaX}px))`;
    draggedAmount = deltaX;
  };

  const handleMouseUpOrLeave = () => {
    if (!isDragging || deviceType !== "mobile") return;
    isDragging = false;
    if (draggedAmount > 50) {
      prevSlide();
    } else if (draggedAmount < -50) {
      nextSlide();
    } else {
      sliderRef.current.style.transform = `translateX(calc(${currentLanguage == "ar" ? "+" : "-"
        }${currentSlide * 100}%))`;
    }
    draggedAmount = 0;
  };

  // Touch Handlers
  const handleTouchStart = (e) => {
    isDragging = true;
    startX = e.touches[0].clientX;
  };

  const handleTouchMove = (e) => {
    if (!isDragging) return;
    const currentX = e.touches[0].clientX;
    const deltaX = currentX - startX;
    sliderRef.current.style.transform = `translateX(calc(${currentLanguage == "ar" ? "+" : "-"
      }${currentSlide * 100}% + ${deltaX}px))`;
    draggedAmount = deltaX;
  };

  const handleTouchEnd = () => {
    if (!isDragging) return;
    isDragging = false;
    if (draggedAmount > 50) {
      prevSlide();
    } else if (draggedAmount < -50) {
      nextSlide();
    } else {
      sliderRef.current.style.transform = `translateX(calc(${currentLanguage == "ar" ? "+" : "-"
        }${currentSlide * 100}%))`;
    }
    draggedAmount = 0;
  };

  return (
    <>
      <Helmet>
        <meta
          name="description"
          content={metaDescription || t("home.default-description")}
        />
        <meta
          name="keywords"
          content={metaKeywords || t("home.default-keywords")}
        />
      </Helmet>
      <section className="product">
        <LoaderOverlay
          loadingState={isProductLoading}
          errorState={isProductError}
          setErrorState={setIsProductError}
          retryFunc={retryProduct}
        />
        <div
          className={[
            "image-gallery",
            imageGalleryOpen ? "active" : "none",
          ].join(" ")}
        >
          <div className="image-gallery-overlay" onClick={closeImage}></div>
          <Close className="close-btn" onClick={closeImage} />
          <img
            className="image-gallery-img"
            src={image}
            alt={`image`}
            ref={imageGallery}
            onClick={zoomMouseClick}
            onMouseMove={zoomMouseMove}
            style={{
              transform:
                currentLanguage == "en"
                  ? "translateX(-50%)"
                  : "translateX(50%)",
            }}
          />
        </div>
        {productData?.length != 0 && !isProductUnavailable ? (
          <>
            <main className="main">
              <div className="images">
                {deviceType === "mobile" ? (
                  <div
                    className="images-slider"
                    onMouseDown={handleMouseDown}
                    onMouseMove={handleMouseMove}
                    onMouseUp={handleMouseUpOrLeave}
                    onMouseLeave={handleMouseUpOrLeave}
                    onTouchStart={handleTouchStart}
                    onTouchMove={handleTouchMove}
                    onTouchEnd={handleTouchEnd}
                  >
                    <div
                      ref={sliderRef}
                      className="images-container"
                      style={{
                        transform: `translateX(calc(${currentLanguage == "ar" ? "+" : "-"
                          }${currentSlide * 100}%))`,
                      }}
                    >
                      {images?.map((img, index) => (
                        <div key={index} className="image-slide">
                          <img
                            src={
                              process.env.REACT_APP_IMAGE_URL +
                              img?.attributes?.url
                            }
                            alt={`image-${index}`}
                            onClick={() => {
                              openImage(
                                process.env.REACT_APP_IMAGE_URL +
                                img?.attributes?.url,
                              );
                            }}
                          />
                        </div>
                      ))}
                    </div>

                    {/* Pagination */}
                    <div className="pagination">
                      {images?.map((_, index) => (
                        <span
                          key={index}
                          className={`dot ${currentSlide === index ? "active" : ""
                            }`}
                          onClick={() => setCurrentSlide(index)}
                        />
                      ))}
                    </div>

                    {/* Navigation Buttons */}
                    <button className="prev-btn" onClick={prevSlide}>
                      &lt;
                    </button>
                    <button className="next-btn" onClick={nextSlide}>
                      &gt;
                    </button>
                  </div>
                ) : (
                  images?.map((img, index) => (
                    <img
                      key={index}
                      src={
                        process.env.REACT_APP_IMAGE_URL + img?.attributes?.url
                      }
                      alt={`image-${index}`}
                      onClick={() => {
                        openImage(
                          process.env.REACT_APP_IMAGE_URL +
                          img?.attributes?.url,
                        );
                      }}
                    />
                  ))
                )}
              </div>

              <div className="info">
                <div className="heading">
                  <div className="title-container">
                    <h1 className="title">{productInfo?.attributes?.Title}</h1>
                    <img
                      className="trademark"
                      src="/img/trademark.png"
                      alt={productInfo?.attributes?.Title}
                    ></img>
                  </div>
                  <p className="subtitle">
                    <span className="keyword">
                      {productInfo?.attributes?.Subtitle}
                    </span>
                  </p>
                  <p className="code">
                    <span className="keyword">{t("product.code")}: </span>
                    <span className="code-text">
                      {barcode ? barcode : productInfo?.attributes?.CODE}
                    </span>
                  </p>
                </div>
                <div className="price">
                  {productInfo?.attributes?.discountedPrice &&
                    productInfo?.attributes?.discountedPrice !==
                    productInfo?.attributes?.Price ? (
                    <div className="real-price">
                      <div className="container">
                        <span className="currency">
                          <UAEDirham />
                        </span>
                        <span className="numerical-price">
                          {productInfo?.attributes?.Price}
                        </span>
                      </div>
                      <span className="vat">{t("home.price-with-vat")}</span>
                    </div>
                  ) : (
                    <></>
                  )}
                  <div className="discounted-price">
                    <div className="container">
                      <span className="currency">
                        <UAEDirham />
                      </span>
                      <span className="numerical-price">
                        {productInfo?.attributes?.discountedPrice ??
                          productInfo?.attributes?.Price}
                      </span>
                    </div>
                    <span className="vat">{t("home.price-with-vat")}</span>
                  </div>
                </div>
                <div
                  className="description"
                  dangerouslySetInnerHTML={{ __html: productInfo?.attributes?.Description || '' }}
                />
                {productInfo?.attributes?.Variants && !isOutOfStock && (
                  <>
                    <div className="options">
                      <ProductOptions
                        variants={productInfo?.attributes?.Variants}
                        selectedButtonId={selectedButtonId}
                        setSelectedButtonId={setSelectedButtonId}
                        setBarcode={setBarcode}
                        setSelectedVariantId={setSelectedVariantId}
                        setIsOutOfStock={setIsOutOfStock}
                      />
                    </div>
                  </>
                )}
                <div className="buttons">
                  <button
                    className="add-to-cart-btn"
                    onClick={addProduct}
                    disabled={isOutOfStock}
                  >
                    {!isOutOfStock
                      ? t("product.add-to-cart")
                      : t("product.out-of-stock")}
                  </button>
                  <button
                    className="instant-buy-btn"
                    onClick={handleInstantBuy}
                    disabled={isOutOfStock || isProcessing}
                  >
                    {t("product.instant-buy")}
                  </button>
                  <Share onClick={toggleShare} className="share-btn" />
                </div>
                {instantBuyInlineError && (
                  <h2 className="login-error" role="alert">
                    {instantBuyInlineError}
                  </h2>
                )}
                {showLimitMessage && (                                  // NEW
                  <p className="stock-limit-message" role="alert">
                    {t("product.limited-quantity")}
                  </p>                                                  // NEW
                )}
                <div className="questions">
                  <h3 className="got-questions">
                    {t("product.got-questions")}
                  </h3>
                  <div className="whatsapp" dir="ltr">
                    <Whatsapp />
                    <a
                      className="whatsapp-link"
                      href="https://api.whatsapp.com/send?phone=971544438476"
                    >
                      +971 54 443 8476
                    </a>
                  </div>
                </div>
                <div className="product-details">
                  <ProductDetails details={productInfo} />
                </div>
                {productInfo?.attributes?.SizeGuide && (
                  <div className="size-table">
                    <SizeTable details={productInfo} />
                  </div>
                )}
              </div>
            </main>
            {!productLoading &&
              !productError &&
              productInfo?.attributes?.RelatedProducts?.data?.length > 0 && (
                <>
                  <hr />
                  <aside className="similar">
                    <FeaturedProducts
                      data={productData}
                      loading={productLoading}
                      error={productError}
                      retryFunc={retryProduct}
                      title={t("product.similar")}
                      link={`/category/${encodeURL(
                        productInfo?.attributes?.Categories?.data[0]?.attributes
                          ?.Title,
                      )}`}
                      type="similar"
                    />
                  </aside>
                </>
              )}
            {isProcessing && (
              <div className="instant-buy-overlay">
                {instantBuyError ? (
                  <div className="instant-buy-overlay__error">
                    <p className="instant-buy-overlay__text">
                      {instantBuyError}
                    </p>
                    <button
                      className="instant-buy-overlay__retry"
                      onClick={() => {
                        setInstantBuyError("");
                        handleInstantBuy();
                      }}
                    >
                      Try Again
                    </button>
                    <button
                      className="instant-buy-overlay__dismiss"
                      onClick={() => setIsProcessing(false)}
                    >
                      Cancel
                    </button>
                  </div>
                ) : (
                  <>
                    <div className="instant-buy-overlay__spinner"></div>
                    <p className="instant-buy-overlay__text">
                      Processing your instant purchase...
                    </p>
                  </>
                )}
              </div>
            )}
          </>
        ) : (
          <>
            <main className="main">
              <h1 className="product-not-found">
                {t("product.product-not-found")}
              </h1>
            </main>
          </>
        )}
      </section>
    </>
  );
};

export default Product;
